import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { LabTestSearch } from '@/components/LabTestSearch';
import { DrugInventorySearch, formatDrugLabel, type InventoryDrug } from '@/components/pharmacy/DrugInventorySearch';
import {
  Search,
  Pill,
  FileText,
  X,
  FlaskConical,
  StickyNote,
  LayoutTemplate,
  Plus,
  Minus,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface Medicine {
  name: string;
  frequency: string;
  duration: string;
  notes?: string;
  drugId?: string;
}

interface ClinicalNote {
  note: string;
}

interface Test {
  id: string;
  name: string;
  code?: string;
  instructions?: string;
  status?: boolean;
}

interface DiseaseTemplate {
  id: string;
  name: string;
  medicines: Medicine[];
  clinicalNotes: ClinicalNote[];
  labTests: Test[];
  doctorId: string;
  createdAt: string;
  updatedAt: string;
}

interface FrontendTemplate extends Omit<DiseaseTemplate, 'labTests'> {
  tests: Test[];
}

interface SelectedPrescription {
  medicines: Medicine[];
  clinicalNotes: ClinicalNote[];
  tests: Test[];
}

interface PrescriptionSectionProps {
  onPrescriptionChange: (data: {
    medicines: { name: string; frequency: string; duration: string; drugId?: string; notes?: string }[];
    tests: { name: string; id: string; instructions?: string }[];
    clinicalNotes: { note: string }[];
    /** Text currently typed in the clinical-note box (not yet clicked "Add note"). */
    pendingClinicalNote?: string;
  }) => void;
  /** Increment to clear lab tests from summary after send-for-billing */
  clearTestsSignal?: number;
  onSendLabTestsForBilling?: () => void;
  isSendingLabTests?: boolean;
  /** Seed medicines / clinical notes when editing an existing diagnosis */
  initialPrescription?: {
    medicines?: Medicine[];
    clinicalNotes?: ClinicalNote[];
  };
}

const FREQUENCY_OPTIONS = [
  'Once a day',
  'Twice a day',
  'Three times a day',
  'Four times a day',
  'Every 4 hours',
  'Every 6 hours',
  'Every 8 hours',
  'Every 12 hours',
  'After meals',
  'Before meals',
  'With meals',
  'At bedtime',
  'As needed',
  'Twice a week',
  'Three times a week',
  'Once weekly',
  'Twice weekly',
] as const;

function emitChange(
  prescription: SelectedPrescription,
  onPrescriptionChange: PrescriptionSectionProps['onPrescriptionChange'],
  pendingClinicalNote?: string
) {
  onPrescriptionChange({
    medicines: prescription.medicines.map((m) => ({
      name: m.name,
      frequency: m.frequency,
      duration: m.duration,
      drugId: m.drugId,
      notes: m.notes,
    })),
    tests: prescription.tests.map((t) => ({
      name: t.name,
      id: t.id,
      instructions: t.instructions,
    })),
    clinicalNotes: prescription.clinicalNotes.map((n) => ({ note: n.note })),
    pendingClinicalNote: pendingClinicalNote ?? '',
  });
}

const PrescriptionSection: React.FC<PrescriptionSectionProps> = ({
  onPrescriptionChange,
  clearTestsSignal,
  onSendLabTestsForBilling,
  isSendingLabTests,
  initialPrescription,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<FrontendTemplate | null>(null);
  const [selectedPrescription, setSelectedPrescription] = useState<SelectedPrescription>({
    medicines: initialPrescription?.medicines ?? [],
    clinicalNotes: initialPrescription?.clinicalNotes ?? [],
    tests: [],
  });
  const [editingTemplate, setEditingTemplate] = useState<FrontendTemplate | null>(null);
  const [hasSeededInitial, setHasSeededInitial] = useState(false);

  const [pickedDrug, setPickedDrug] = useState<InventoryDrug | null>(null);
  const [medicineDuration, setMedicineDuration] = useState('');
  const [medicineFrequency, setMedicineFrequency] = useState<string>('Once a day');

  const [customMedicine, setCustomMedicine] = useState({
    name: '',
    duration: '',
    frequency: 'Once a day',
    notes: '',
  });
  const [catalogueTest, setCatalogueTest] = useState<Test | null>(null);
  const [customLabTest, setCustomLabTest] = useState({ name: '', instructions: '' });
  const [customClinicalNote, setCustomClinicalNote] = useState<ClinicalNote>({ note: '' });

  useEffect(() => {
    if (hasSeededInitial || !initialPrescription) return;
    const seeded: SelectedPrescription = {
      medicines: initialPrescription.medicines ?? [],
      clinicalNotes: initialPrescription.clinicalNotes ?? [],
      tests: [],
    };
    setSelectedPrescription(seeded);
    emitChange(seeded, onPrescriptionChange, '');
    setHasSeededInitial(true);
  }, [initialPrescription, hasSeededInitial, onPrescriptionChange]);

  useEffect(() => {
    if (clearTestsSignal && clearTestsSignal > 0) {
      setSelectedPrescription((prev) => {
        const next = { ...prev, tests: [] };
        emitChange(next, onPrescriptionChange, customClinicalNote.note);
        return next;
      });
    }
  }, [clearTestsSignal, onPrescriptionChange]);

  const { data: templates, isLoading } = useQuery<DiseaseTemplate[]>({
    queryKey: ['prescription-templates'],
    queryFn: async () => {
      const response = await api.get('/api/doctor/get-prescription-templates');
      return response.data?.data;
    },
  });

  const filteredTemplates =
    templates?.filter((template) => {
      if (!searchQuery.trim()) return true;
      return template.name.toLowerCase().includes(searchQuery.toLowerCase());
    }) || [];

  const updatePrescription = (next: SelectedPrescription, pendingNote = customClinicalNote.note) => {
    setSelectedPrescription(next);
    emitChange(next, onPrescriptionChange, pendingNote);
  };

  const isMedicineSelected = (medicine: Medicine) =>
    selectedPrescription.medicines.some(
      (m) =>
        m.name === medicine.name &&
        m.duration === medicine.duration &&
        m.frequency === medicine.frequency
    );

  const isTestSelected = (test: Test) => selectedPrescription.tests.some((t) => t.id === test.id || t.name === test.name);

  const handleAddMedicine = (medicine: Medicine) => {
    if (isMedicineSelected(medicine)) {
      updatePrescription({
        ...selectedPrescription,
        medicines: selectedPrescription.medicines.filter(
          (m) =>
            !(
              m.name === medicine.name &&
              m.duration === medicine.duration &&
              m.frequency === medicine.frequency
            )
        ),
      });
    } else {
      updatePrescription({
        ...selectedPrescription,
        medicines: [...selectedPrescription.medicines, medicine],
      });
    }
  };

  const handleAddTest = (test: Test) => {
    if (isTestSelected(test)) {
      updatePrescription({
        ...selectedPrescription,
        tests: selectedPrescription.tests.filter((t) => t.id !== test.id && t.name !== test.name),
      });
    } else {
      updatePrescription({
        ...selectedPrescription,
        tests: [...selectedPrescription.tests, test],
      });
    }
  };

  const handleRemoveMedicine = (index: number) => {
    updatePrescription({
      ...selectedPrescription,
      medicines: selectedPrescription.medicines.filter((_, i) => i !== index),
    });
  };

  const handleRemoveTest = (index: number) => {
    updatePrescription({
      ...selectedPrescription,
      tests: selectedPrescription.tests.filter((_, i) => i !== index),
    });
  };

  const handleRemoveClinicalNote = (index: number) => {
    updatePrescription({
      ...selectedPrescription,
      clinicalNotes: selectedPrescription.clinicalNotes.filter((_, i) => i !== index),
    });
  };

  const handleSelectDisease = (template: DiseaseTemplate) => {
    const frontendTemplate: FrontendTemplate = { ...template, tests: template.labTests };
    setSelectedTemplate(frontendTemplate);
    setEditingTemplate(frontendTemplate);
    setSearchQuery('');
    setTemplateDropdownOpen(false);
  };

  const handleEditMedicine = (index: number, field: keyof Medicine, value: string) => {
    if (!editingTemplate) return;
    const updatedMedicines = [...editingTemplate.medicines];
    updatedMedicines[index] = { ...updatedMedicines[index], [field]: value };
    setEditingTemplate({ ...editingTemplate, medicines: updatedMedicines });
  };

  const handleEditTest = (index: number, field: keyof Test, value: string) => {
    if (!editingTemplate) return;
    const updatedTests = [...editingTemplate.tests];
    updatedTests[index] = { ...updatedTests[index], [field]: value };
    setEditingTemplate({ ...editingTemplate, tests: updatedTests });
  };

  const handleSaveTemplate = () => {
    if (editingTemplate) setSelectedTemplate(editingTemplate);
  };

  const handleInventoryMedicineAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pickedDrug) {
      toast.error('Select a medicine from pharmacy inventory.');
      return;
    }
    if (!medicineDuration.trim()) {
      toast.error('Enter duration in days.');
      return;
    }
    const medicine: Medicine = {
      name: formatDrugLabel(pickedDrug),
      drugId: pickedDrug.id,
      frequency: medicineFrequency,
      duration: medicineDuration,
    };
    updatePrescription({
      ...selectedPrescription,
      medicines: [...selectedPrescription.medicines, medicine],
    });
    setPickedDrug(null);
    setMedicineDuration('');
    setMedicineFrequency('Once a day');
    toast.success('Medicine added to prescription');
  };

  const handleCustomMedicineSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = customMedicine.name.trim();
    if (!name) {
      toast.error('Enter medicine name.');
      return;
    }
    if (!customMedicine.duration.trim()) {
      toast.error('Enter duration in days.');
      return;
    }
    const medicine: Medicine = {
      name,
      frequency: customMedicine.frequency,
      duration: customMedicine.duration.trim(),
      notes: customMedicine.notes.trim() || undefined,
    };
    updatePrescription({
      ...selectedPrescription,
      medicines: [...selectedPrescription.medicines, medicine],
    });
    setCustomMedicine({ name: '', duration: '', frequency: 'Once a day', notes: '' });
    toast.success('Custom medicine added to prescription');
  };

  const handleCatalogueTestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!catalogueTest?.name || !catalogueTest.id) return;
    updatePrescription({
      ...selectedPrescription,
      tests: [...selectedPrescription.tests, catalogueTest],
    });
    setCatalogueTest(null);
    toast.success('Lab test added to prescription');
  };

  const handleCustomLabTestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = customLabTest.name.trim();
    if (!name) {
      toast.error('Enter lab test name.');
      return;
    }
    const test: Test = {
      name,
      id: `custom-${Date.now()}`,
      instructions: customLabTest.instructions.trim() || undefined,
    };
    updatePrescription({
      ...selectedPrescription,
      tests: [...selectedPrescription.tests, test],
    });
    setCustomLabTest({ name: '', instructions: '' });
    toast.success('Custom lab test added to prescription');
  };

  const handleCustomClinicalNoteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customClinicalNote.note.trim()) return;
    updatePrescription({
      ...selectedPrescription,
      clinicalNotes: [...selectedPrescription.clinicalNotes, customClinicalNote],
    });
    setCustomClinicalNote({ note: '' });
  };

  const handleTestSelection = (test: Test) => {
    setCatalogueTest({ name: test.name, id: test.id, instructions: '' });
  };

  const isCustomTestId = (id: string) => !id || id.startsWith('custom-');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-teal-600 border-t-transparent" />
        <span className="ml-3 text-slate-600">Loading templates…</span>
      </div>
    );
  }

  const SummaryBlock = ({
    title,
    icon: Icon,
    count,
    emptyLabel,
    children,
    accent,
  }: {
    title: string;
    icon: React.ElementType;
    count: number;
    emptyLabel: string;
    children: React.ReactNode;
    accent: string;
  }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={cn('flex h-7 w-7 items-center justify-center rounded-lg', accent)}>
            <Icon className="h-3.5 w-3.5 text-white" />
          </div>
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        </div>
        <Badge variant="secondary" className="font-mono text-xs">
          {count}
        </Badge>
      </div>
      {count === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-3 py-6 text-center text-xs text-slate-500">
          {emptyLabel}
        </p>
      ) : (
        <div className="space-y-1.5">{children}</div>
      )}
    </div>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-5 lg:items-start">
      {/* Builder column */}
      <div className="space-y-4 lg:col-span-3">
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Spell-check is on for free-text fields (custom medicine, lab instructions, clinical notes).
          Misspelled words are underlined — right-click for suggestions (browser dependent). Catalogue
          drug/lab picks are not spell-checked.
        </p>
        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <LayoutTemplate className="h-5 w-5 text-teal-600" />
              <div>
                <CardTitle className="text-base">Disease templates</CardTitle>
                <CardDescription>Load a saved protocol, then add items to the prescription</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search disease templates…"
                className="pl-9"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setTemplateDropdownOpen(true);
                }}
                onFocus={() => setTemplateDropdownOpen(true)}
                onBlur={() => setTimeout(() => setTemplateDropdownOpen(false), 200)}
              />
              {templateDropdownOpen && filteredTemplates.length > 0 && (
                <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                  {filteredTemplates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      className="flex w-full items-center justify-between border-b border-slate-50 px-3 py-2.5 text-left text-sm last:border-0 hover:bg-teal-50"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectDisease(template);
                      }}
                    >
                      <span className="font-medium text-slate-800">{template.name}</span>
                      <span className="text-xs text-slate-500">
                        {template.medicines.length} med · {template.labTests.length} lab
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {selectedTemplate && editingTemplate && (
          <Card className="border-teal-100 bg-gradient-to-br from-teal-50/40 to-white shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base text-teal-900">{selectedTemplate.name}</CardTitle>
              <Button type="button" size="sm" variant="outline" onClick={handleSaveTemplate}>
                Save edits
              </Button>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Medicines</p>
                <div className="space-y-2">
                  {editingTemplate.medicines.map((medicine, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-12 items-center gap-2 rounded-lg border border-slate-100 bg-white p-2"
                    >
                      <Input
                        className="col-span-5 h-8 text-sm"
                        value={medicine.name}
                        onChange={(e) => handleEditMedicine(index, 'name', e.target.value)}
                      />
                      <Input
                        className="col-span-2 h-8 text-sm"
                        value={medicine.duration}
                        onChange={(e) => handleEditMedicine(index, 'duration', e.target.value)}
                      />
                      <select
                        className="col-span-3 h-8 rounded-md border border-input bg-background px-2 text-sm"
                        value={medicine.frequency}
                        onChange={(e) => handleEditMedicine(index, 'frequency', e.target.value)}
                      >
                        {FREQUENCY_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        size="icon"
                        variant={isMedicineSelected(medicine) ? 'destructive' : 'secondary'}
                        className="col-span-2 h-8 w-full"
                        onClick={() => handleAddMedicine(medicine)}
                      >
                        {isMedicineSelected(medicine) ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Lab tests</p>
                <div className="space-y-2">
                  {editingTemplate.tests.map((test, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-12 items-center gap-2 rounded-lg border border-slate-100 bg-white p-2"
                    >
                      <Input
                        className="col-span-6 h-8 text-sm"
                        value={test.name}
                        onChange={(e) => handleEditTest(index, 'name', e.target.value)}
                      />
                      <Input
                        className="col-span-4 h-8 text-sm"
                        placeholder="Instructions"
                        value={test.instructions || ''}
                        onChange={(e) => handleEditTest(index, 'instructions', e.target.value)}
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant={isTestSelected(test) ? 'destructive' : 'secondary'}
                        className="col-span-2 h-8 w-full"
                        onClick={() => handleAddTest(test)}
                      >
                        {isTestSelected(test) ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              {editingTemplate.clinicalNotes?.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Clinical notes</p>
                  {editingTemplate.clinicalNotes.map((note, index) => {
                    const selected = selectedPrescription.clinicalNotes.some((n) => n.note === note.note);
                    return (
                      <div
                        key={index}
                        className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-amber-100 bg-amber-50/50 p-2 text-sm"
                      >
                        <span className="flex-1 text-slate-700">{note.note}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant={selected ? 'destructive' : 'outline'}
                          onClick={() => {
                            if (selected) {
                              updatePrescription({
                                ...selectedPrescription,
                                clinicalNotes: selectedPrescription.clinicalNotes.filter((n) => n.note !== note.note),
                              });
                            } else {
                              updatePrescription({
                                ...selectedPrescription,
                                clinicalNotes: [...selectedPrescription.clinicalNotes, { note: note.note }],
                              });
                            }
                          }}
                        >
                          {selected ? 'Remove' : 'Add'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="border-emerald-100 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Pill className="h-5 w-5 text-emerald-600" />
              <div>
                <CardTitle className="text-base">Add medicine</CardTitle>
                <CardDescription>Search hospital pharmacy inventory</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInventoryMedicineAdd} className="space-y-3">
              <DrugInventorySearch
                selectedLabel={pickedDrug ? formatDrugLabel(pickedDrug) : undefined}
                onClear={() => setPickedDrug(null)}
                onDrugSelect={setPickedDrug}
                className="w-full"
              />
              {pickedDrug && (
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">Duration (days)</label>
                    <Input
                      type="number"
                      min={1}
                      placeholder="e.g. 5"
                      value={medicineDuration}
                      onChange={(e) => setMedicineDuration(e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-slate-600">Frequency</label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={medicineFrequency}
                      onChange={(e) => setMedicineFrequency(e.target.value)}
                    >
                      {FREQUENCY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={!pickedDrug}>
                Add to prescription
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-emerald-200/80 border-dashed shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Pill className="h-5 w-5 text-emerald-500" />
              <div>
                <CardTitle className="text-base">Custom medicine</CardTitle>
                <CardDescription>
                  Medicine not in hospital pharmacy — name only (e.g. outside purchase)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCustomMedicineSubmit} className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Medicine name</label>
                <textarea
                  placeholder="e.g. Tab. XYZ 500mg"
                  rows={2}
                  value={customMedicine.name}
                  spellCheck
                  lang="en"
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  className="min-h-[64px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onChange={(e) => setCustomMedicine((m) => ({ ...m, name: e.target.value }))}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Duration (days)</label>
                  <Input
                    type="number"
                    min={1}
                    placeholder="e.g. 5"
                    value={customMedicine.duration}
                    onChange={(e) => setCustomMedicine((m) => ({ ...m, duration: e.target.value }))}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-slate-600">Frequency</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={customMedicine.frequency}
                    onChange={(e) => setCustomMedicine((m) => ({ ...m, frequency: e.target.value }))}
                  >
                    {FREQUENCY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Notes (optional)</label>
                <textarea
                  placeholder="e.g. After food, avoid alcohol"
                  rows={2}
                  value={customMedicine.notes}
                  spellCheck
                  lang="en"
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  className="min-h-[64px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onChange={(e) => setCustomMedicine((m) => ({ ...m, notes: e.target.value }))}
                />
              </div>
              <Button type="submit" variant="outline" className="border-emerald-300 text-emerald-900 hover:bg-emerald-50">
                Add custom medicine
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-blue-100 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-blue-600" />
              <div>
                <CardTitle className="text-base">Add lab test</CardTitle>
                <CardDescription>Search the lab catalogue — click outside or Close to dismiss</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {catalogueTest?.name ? (
              <form onSubmit={handleCatalogueTestSubmit} className="flex flex-wrap items-center gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-blue-200 bg-blue-50/50 px-3 py-2">
                  <span className="truncate text-sm font-medium text-slate-800">{catalogueTest.name}</span>
                  <button
                    type="button"
                    className="text-slate-400 hover:text-red-500"
                    onClick={() => setCatalogueTest(null)}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Add to prescription
                </Button>
              </form>
            ) : (
              <LabTestSearch onTestSelect={handleTestSelection} placeholder="Search lab test…" className="w-full" />
            )}
          </CardContent>
        </Card>

        <Card className="border-blue-200/80 border-dashed shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-blue-500" />
              <div>
                <CardTitle className="text-base">Custom lab test</CardTitle>
                <CardDescription>
                  Test not in hospital catalogue — enter name manually
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCustomLabTestSubmit} className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Test name</label>
                <textarea
                  placeholder="e.g. MRI Brain with contrast"
                  rows={2}
                  value={customLabTest.name}
                  spellCheck
                  lang="en"
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  className="min-h-[64px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onChange={(e) => setCustomLabTest((t) => ({ ...t, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Instructions (optional)</label>
                <textarea
                  placeholder="e.g. Fasting required"
                  rows={2}
                  value={customLabTest.instructions}
                  spellCheck
                  lang="en"
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  className="min-h-[64px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onChange={(e) => setCustomLabTest((t) => ({ ...t, instructions: e.target.value }))}
                />
              </div>
              <Button type="submit" variant="outline" className="border-blue-300 text-blue-900 hover:bg-blue-50">
                Add custom lab test
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-amber-100 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <StickyNote className="h-5 w-5 text-amber-600" />
              <CardTitle className="text-base">Clinical note</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCustomClinicalNoteSubmit} className="space-y-3">
              <textarea
                placeholder="Instructions for patient or pharmacy…"
                className="min-h-[88px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={customClinicalNote.note}
                spellCheck
                lang="en"
                autoCorrect="on"
                autoCapitalize="sentences"
                onChange={(e) => {
                  const note = e.target.value;
                  setCustomClinicalNote({ note });
                  emitChange(selectedPrescription, onPrescriptionChange, note);
                }}
              />
              <p className="text-[11px] text-slate-500">
                Included on the printed prescription when you save. Click Add note to keep multiple notes, or just Save consultation.
              </p>
              <Button type="submit" variant="outline" className="border-amber-200 text-amber-900 hover:bg-amber-50">
                Add note
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Summary column */}
      <div className="lg:col-span-2 lg:sticky lg:top-24">
        <Card className="border-slate-200 shadow-md">
          <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-t-lg">
            <CardTitle className="text-base font-semibold">Prescription summary</CardTitle>
            <CardDescription className="text-slate-300">
              {selectedPrescription.medicines.length +
                selectedPrescription.tests.length +
                selectedPrescription.clinicalNotes.length}{' '}
              items selected
            </CardDescription>
          </CardHeader>
          <CardContent className="max-h-[calc(100vh-12rem)] space-y-5 overflow-y-auto p-4">
            <SummaryBlock
              title="Medicines"
              icon={Pill}
              count={selectedPrescription.medicines.length}
              emptyLabel="No medicines added"
              accent="bg-emerald-600"
            >
              {selectedPrescription.medicines.map((medicine, index) => (
                <div
                  key={`${medicine.drugId ?? medicine.name}-${index}`}
                  className="flex items-start justify-between gap-2 rounded-lg border border-emerald-100 bg-emerald-50/40 p-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">{medicine.name}</p>
                    <p className="text-xs text-slate-600">
                      {medicine.duration} days · {medicine.frequency}
                    </p>
                    {medicine.notes && (
                      <p className="truncate text-xs text-slate-500">{medicine.notes}</p>
                    )}
                    {medicine.drugId ? (
                      <p className="mt-0.5 text-[10px] text-emerald-700">Linked to inventory</p>
                    ) : (
                      <p className="mt-0.5 text-[10px] text-slate-500">Custom · not in inventory</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="shrink-0 text-slate-400 hover:text-red-600"
                    onClick={() => handleRemoveMedicine(index)}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </SummaryBlock>

            <SummaryBlock
              title="Lab tests"
              icon={FlaskConical}
              count={selectedPrescription.tests.length}
              emptyLabel="No lab tests added"
              accent="bg-blue-600"
            >
              {selectedPrescription.tests.map((test, index) => (
                <div
                  key={test.id || index}
                  className="flex items-start justify-between gap-2 rounded-lg border border-blue-100 bg-blue-50/40 p-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">{test.name}</p>
                    {test.instructions && (
                      <p className="truncate text-xs text-slate-600">{test.instructions}</p>
                    )}
                    {isCustomTestId(test.id) && (
                      <p className="mt-0.5 text-[10px] text-slate-500">Custom · not in catalogue</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="shrink-0 text-slate-400 hover:text-red-600"
                    onClick={() => handleRemoveTest(index)}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {selectedPrescription.tests.length > 0 && onSendLabTestsForBilling && (
                <div className="mt-3 space-y-2 border-t border-blue-100 pt-3">
                  <p className="text-xs leading-relaxed text-slate-600">
                    Send only these tests to reception for billing. After payment, the lab will process them and reports will appear below.
                  </p>
                  <Button
                    type="button"
                    onClick={onSendLabTestsForBilling}
                    disabled={isSendingLabTests}
                    className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                  >
                    <FlaskConical className="h-4 w-4" />
                    {isSendingLabTests ? 'Sending to reception…' : 'Send lab tests for billing'}
                  </Button>
                </div>
              )}
            </SummaryBlock>

            <SummaryBlock
              title="Clinical notes"
              icon={FileText}
              count={selectedPrescription.clinicalNotes.length}
              emptyLabel="No notes added"
              accent="bg-amber-600"
            >
              {selectedPrescription.clinicalNotes.map((note, index) => (
                <div
                  key={index}
                  className="flex items-start justify-between gap-2 rounded-lg border border-amber-100 bg-amber-50/50 p-2.5"
                >
                  <p className="flex-1 text-xs leading-relaxed text-slate-800">{note.note}</p>
                  <button
                    type="button"
                    className="shrink-0 text-slate-400 hover:text-red-600"
                    onClick={() => handleRemoveClinicalNote(index)}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </SummaryBlock>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrescriptionSection;
